import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { ArrowRight, Check, ChevronDown, ShieldCheck } from 'lucide-react';

import { PageMetadata } from '@/components/PageMetadata';
import {
  DeveloperGraphic,
  PaymentsGraphic,
  ProtocolGraphic,
  TreasuryGraphic,
} from '@/components/sections/Capabilities';
import { TrustStrip } from '@/components/sections/TrustStrip';
import { buttonVariants } from '@/components/ui/button';
import { CalendlyWidget } from '@/components/ui/calendly-widget';
import { Container } from '@/components/ui/container';
import { getIcon } from '@/components/ui/icon-map';
import { Logo } from '@/components/ui/logo';
import { OpenAppButton } from '@/components/ui/open-app-dialog';
import {
  getStarted,
  type AdCampaign,
  type AdUseCaseId,
} from '@/content/get-started';
import { trackWebsiteEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

const DEMO_ID = 'book-demo';
const VISUALS = {
  treasury: TreasuryGraphic,
  payments: PaymentsGraphic,
  wallets: DeveloperGraphic,
  protocols: ProtocolGraphic,
};

function UseCaseAccordion({
  title,
  description,
  useCase,
}: {
  title: string;
  description: string;
  useCase: AdUseCaseId;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div>
      <button
        id={`${id}-trigger`}
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-content`}
        onClick={() => {
          trackWebsiteEvent('ad_detail_toggled', {
            ad_selected_use_case: useCase,
            detail_title: title,
            expanded: !open,
          });
          setOpen((value) => !value);
        }}
        className="flex w-full items-center justify-between gap-4 rounded-sm py-4 text-left text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ember-500/50"
      >
        {title}
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-ink-400 transition-transform duration-300 motion-reduce:transition-none',
            open && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>
      {/* Keep the panel mounted so its height can slide in both directions.
          Collapsed copy remains outside keyboard and screen-reader navigation. */}
      <div
        id={`${id}-content`}
        role="region"
        aria-labelledby={`${id}-trigger`}
        aria-hidden={!open}
        inert={!open ? true : undefined}
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <p className="pb-4 pr-5 text-sm leading-6 text-ink-500">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

function UseCases({
  selected,
  onSelect,
}: {
  selected: AdUseCaseId;
  onSelect: (useCase: AdUseCaseId) => void;
}) {
  const useCase =
    getStarted.useCases.find((item) => item.id === selected) ??
    getStarted.useCases[0];
  const Graphic = VISUALS[selected];

  return (
    <section
      aria-labelledby="use-cases-title"
      className="border-t border-ember-100/60 bg-gradient-to-br from-white via-ember-50/60 to-ember-100/70 py-16 sm:py-24"
    >
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <h2
            id="use-cases-title"
            className="text-balance text-3xl font-medium leading-tight tracking-tight sm:text-4xl"
          >
            {getStarted.useCasesTitle}
          </h2>
          <p className="mt-4 text-base leading-7 text-ink-500">
            {getStarted.useCasesDescription}
          </p>
        </div>

        <div
          role="group"
          aria-label="What would you like to do with Den?"
          className="mx-auto mt-8 flex max-w-2xl flex-wrap justify-center gap-2 sm:grid sm:grid-cols-2 lg:flex lg:max-w-none"
        >
          {getStarted.useCases.map((item) => {
            const Icon = getIcon(item.icon);
            return (
              <button
                key={item.id}
                type="button"
                aria-pressed={selected === item.id}
                data-ad-use-case={item.id}
                aria-controls="use-case-details"
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'h-auto min-h-11 whitespace-normal px-5 py-3',
                  selected === item.id &&
                    'border-ink-900 bg-ink-900 text-white hover:bg-ink-800 hover:text-white'
                )}
                onClick={() => {
                  if (selected === item.id) return;
                  onSelect(item.id);
                  trackWebsiteEvent('ad_use_case_selected', {
                    use_case: item.id,
                    ad_selected_use_case: item.id,
                    previous_use_case: selected,
                  });
                }}
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </div>

        <div
          id="use-case-details"
          className="mt-8 overflow-hidden rounded-3xl border border-hairline bg-white shadow-soft"
        >
          <div className="grid lg:grid-cols-2">
            <div className="p-6 sm:p-10">
              <p className="text-sm font-medium text-ink-500">
                {useCase.eyebrow}
              </p>
              <h3
                className="mt-3 text-balance text-2xl font-medium leading-snug tracking-tight sm:text-3xl"
                aria-live="polite"
              >
                {useCase.title}
              </h3>
              <p className="mt-4 text-sm leading-7 text-ink-500">
                {useCase.description}
              </p>
              <div key={useCase.id} className="mt-6 divide-y divide-hairline">
                {useCase.points.map((point) => (
                  <UseCaseAccordion
                    key={point.title}
                    title={point.title}
                    description={point.description}
                    useCase={selected}
                  />
                ))}
              </div>
              <a
                href={`#${DEMO_ID}`}
                onClick={() =>
                  trackWebsiteEvent('ad_demo_cta_clicked', {
                    cta_location: 'ad_use_case',
                    ad_selected_use_case: selected,
                  })
                }
                className={cn(buttonVariants({ size: 'lg' }), 'mt-6')}
              >
                Book a demo <ArrowRight className="size-4" aria-hidden="true" />
              </a>
            </div>
            <div
              role="img"
              aria-label={`${useCase.eyebrow} product illustration`}
              className="flex min-w-0 flex-col justify-center gap-6 border-t border-hairline bg-surface p-5 sm:p-10 lg:border-l lg:border-t-0"
            >
              <div aria-hidden="true" className="pointer-events-none">
                <Graphic />
              </div>
              <div className="flex items-center justify-center gap-2 text-xs text-ink-500">
                <ShieldCheck className="size-4" aria-hidden="true" />
                Independent policy checks. At every layer.
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

/** Focused campaign destination, with the scheduler alongside the product pitch. */
export function GetStarted({ campaign }: { campaign: AdCampaign }) {
  const { pathname } = useLocation();
  const [selected, setSelected] = useState<AdUseCaseId>(
    campaign.initialUseCase
  );
  const viewed = useRef(false);

  useEffect(() => {
    // Strict Mode replays effects; a route visit still counts only once.
    if (viewed.current) return;
    viewed.current = true;
    trackWebsiteEvent('ad_landing_viewed', {
      ad_selected_use_case: campaign.initialUseCase,
    });
  }, [campaign.initialUseCase]);
  return (
    <div
      className="min-h-screen bg-white text-ink-900 antialiased"
      data-ad-variant={campaign.id}
    >
      <PageMetadata />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-white focus:p-3"
      >
        Skip to content
      </a>
      <header className="border-b border-hairline bg-white">
        <Container className="flex h-20 items-center justify-between gap-4">
          <Link to="/" aria-label="Den home">
            <Logo className="h-9" />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-ink-500 sm:block">
              Already using Den?
            </span>
            <OpenAppButton variant="link" analyticsLocation="ad_header">
              Log in <ArrowRight className="size-4" aria-hidden="true" />
            </OpenAppButton>
          </div>
        </Container>
      </header>

      <main id="main-content">
        <section className="bg-surface py-10 sm:py-14 lg:py-16">
          <Container className="grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-16">
            <div className="py-2 lg:py-6">
              <p className="flex items-center gap-2.5 text-xs font-medium uppercase tracking-[0.14em] text-ink-500">
                <span
                  className="size-1.5 rounded-full bg-ember-500"
                  aria-hidden="true"
                />
                {getStarted.eyebrow}
              </p>
              <h1 className="mt-6 max-w-lg text-balance text-[2.8rem] font-medium leading-[1.07] tracking-[-0.045em] sm:text-6xl lg:text-[3.8rem]">
                {getStarted.title}
              </h1>
              <p className="mt-6 max-w-md text-lg leading-8 text-ink-500">
                {getStarted.description}
              </p>
              <a
                href={`#${DEMO_ID}`}
                onClick={() =>
                  trackWebsiteEvent('ad_demo_cta_clicked', {
                    cta_location: 'ad_hero',
                    ad_selected_use_case: selected,
                  })
                }
                className={cn(buttonVariants({ size: 'lg' }), 'mt-6 lg:hidden')}
              >
                Book a demo <ArrowRight className="size-4" aria-hidden="true" />
              </a>
              <p className="mt-9 text-sm font-medium">
                In your personalized demo:
              </p>
              <ul className="mt-4 space-y-3.5">
                {getStarted.benefits.map((benefit) => (
                  <li
                    key={benefit}
                    className="flex items-start gap-3 text-sm leading-6 text-ink-600"
                  >
                    <Check
                      className="mt-1 size-4 shrink-0 text-ink-900"
                      aria-hidden="true"
                    />
                    {benefit}
                  </li>
                ))}
              </ul>
              <div className="mt-10 grid grid-cols-3 gap-3 border-t border-ink-200/70 pt-7 sm:gap-5">
                {getStarted.trust.map((item) => {
                  const Icon = getIcon(item.icon);
                  return (
                    <div key={item.title}>
                      <Icon
                        className="size-5 text-ink-600"
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                      <p className="mt-3 text-xs font-medium leading-5">
                        {item.title}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-ink-500">
                        {item.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <section
              id={DEMO_ID}
              aria-labelledby="demo-title"
              className="scroll-mt-6 overflow-hidden rounded-3xl border border-hairline bg-white shadow-soft"
            >
              <div className="border-b border-hairline px-6 py-5 text-center sm:px-8">
                <h2
                  id="demo-title"
                  className="text-2xl font-medium tracking-tight"
                >
                  {getStarted.demoTitle}
                </h2>
                <p className="mx-auto mt-3 max-w-xs text-sm leading-6 text-ink-500">
                  {getStarted.demoDescription}
                </p>
              </div>
              <CalendlyWidget
                active
                trackOpenOnView
                autoResize
                analyticsContext={{
                  cta_location: 'ad_hero',
                  demo_page_path: pathname,
                  ad_selected_use_case: selected,
                }}
              />
              <p className="border-t border-hairline px-6 py-4 text-center text-xs leading-5 text-ink-500">
                Your information is handled under our{' '}
                <Link to="/privacy" className="underline underline-offset-2">
                  Privacy Policy
                </Link>
                .
              </p>
            </section>
          </Container>
        </section>
        <TrustStrip />
        <UseCases selected={selected} onSelect={setSelected} />
      </main>

      <footer className="border-t border-hairline py-7">
        <Container className="flex flex-col items-center justify-between gap-4 text-xs text-ink-500 sm:flex-row">
          <p>© {new Date().getFullYear()} Den Technologies Inc.</p>
          <nav aria-label="Legal" className="flex gap-6">
            <Link to="/terms" className="hover:text-ink-900">
              Terms of Use
            </Link>
            <Link to="/privacy" className="hover:text-ink-900">
              Privacy Policy
            </Link>
          </nav>
        </Container>
      </footer>
    </div>
  );
}
